import Stripe from 'stripe';
import { getFinalPriceAmountAfterCoupon } from './price-helpers';
import { PaymentParams } from './types/PaymentParams';

export type createStripePaymentIntentResponse =
  | {
      id: Stripe.PaymentIntent['id'];
      client_secret: Stripe.PaymentIntent['client_secret'];
    }
  | undefined;

/**
 * @deprecated Use saturn endpoints instead for creating one time purchase, creating a subscription or updating a subscription.
 */
export const createStripePaymentIntent = async ({
  stripeInstance,
  customerId,
  price,
  coupon,
  metadata,
  paymentMethodTypes,
}: PaymentParams): Promise<createStripePaymentIntentResponse> => {
  if (price.type === 'one_time') {
    return await createPaymentForOneTimePurchase({
      stripeInstance,
      customerId,
      price,
      coupon,
      metadata,
      paymentMethodTypes,
    });
  }
  return await createPaymentForSubscription({
    stripeInstance,
    customerId,
    price,
    coupon,
    metadata,
    paymentMethodTypes,
  });
};

export const createPaymentForOneTimePurchase = async ({
  stripeInstance,
  customerId,
  price,
  coupon,
  metadata,
  paymentMethodTypes,
}: PaymentParams) => {
  if (!price.unit_amount) {
    return;
  }

  // Apply coupon to the price is not supported by stripe for one time purchases so we must do it manually.
  const finalPriceAmount = getFinalPriceAmountAfterCoupon({
    amount: price.unit_amount,
    coupon,
    currency: price.currency,
  });

  const paymentIntent = await stripeInstance.paymentIntents.create({
    customer: customerId,
    amount: finalPriceAmount,
    currency: price.currency,
    metadata,
    payment_method_types: paymentMethodTypes,
  });

  return {
    id: paymentIntent.id,
    client_secret: paymentIntent.client_secret,
  };
};

const createPaymentForSubscription = async ({
  stripeInstance,
  customerId,
  price,
  coupon,
  metadata,
  paymentMethodTypes,
}: PaymentParams) => {
  const subscription = await stripeInstance.subscriptions.create({
    customer: customerId,
    currency: price.currency,
    payment_behavior: 'default_incomplete',
    items: [{ price: price.id, quantity: 1 }],
    payment_settings: {
      save_default_payment_method: 'on_subscription',
      payment_method_types: paymentMethodTypes as Stripe.Emptyable<
        Stripe.SubscriptionCreateParams.PaymentSettings.PaymentMethodType[]
      >,
    },
    expand: ['latest_invoice.confirmation_secret'],
    metadata,
    discounts: [{ coupon: coupon?.id }], // Coupon will automatically get applied to the subscription if it's valid
    automatic_tax: { enabled: true },
  });

  const invoice = subscription.latest_invoice as Stripe.Invoice | null;

  if (!invoice || !invoice.confirmation_secret || !invoice.confirmation_secret.client_secret) {
    // If we are here it is because the subscription does not require immediate payment.
    // This should not happen with our current setup.
    return undefined;
  }

  const clientSecret = invoice.confirmation_secret.client_secret;
  const paymentIntentId = clientSecret.split('_secret')[0] as string; //if this secret is present, it will always have the format `pi_123_secret_abc`

  // Ensure the PaymentIntent carries the same metadata as the subscription
  await stripeInstance.paymentIntents.update(paymentIntentId, {
    metadata,
  });

  return {
    id: paymentIntentId,
    client_secret: clientSecret,
  };
};
